import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { catchError, finalize, map, Observable, of, throwError } from 'rxjs';

import { ToastService } from '@reg/shared/service';
import { CookieService } from 'ngx-cookie';
import { SpinnerService } from '@reg/shared/spinner/spinner.service';
import { ConfigurationService } from '@reg/shared/service/configuration.service';
import { IPagedResponse, JsonApiModel, PagedResponse } from '@reg/shared/model';
import { IJsonApiResponse, IKeyValue } from '../json-api/json-api.interface';

export interface ICacheData {
    url: string;
    expires: number;
    data: PagedResponse;
}

/**
 * Allows us to centralize commonly used methods and reduce duplication in service classes
 */
@Injectable()
export class HttpServiceHelper {
    // The number of milliseconds to cache data
    public static CACHE_TIME: number = 1200000; // 20 minutes in milliseconds
    private cachedMap: { [typeName: string]: ICacheData[]; } = {};

    public static getErrorMessage(error: HttpErrorResponse): string {
        // in a real world app, we may send the server to some remote logging infrastructure
        // instead of just logging it to the console
        let errorMsg: string = null;
        const errorJson: any = error?.error;

        if (errorJson.error) {
            errorMsg = errorJson.error.detail || errorJson.error.title;
        } else if (errorJson.errors?.length > 0) {
            const firstError: any = errorJson.errors[0];
            errorMsg = firstError.detail || firstError.title;
        } else {
            for (const name in errorJson.errors) {
                if (errorJson.errors.hasOwnProperty(name)) {
                    errorMsg = errorJson.errors[name];
                    break;
                }
            }
        }

        if (!errorMsg) {
            errorMsg = 'Server Error';
        }

        return errorMsg;
    }

    constructor(
                public http: HttpClient,
                public cookieService: CookieService,
                public spinnerService: SpinnerService,
                public toastService: ToastService,
                public configService: ConfigurationService,
    ) {
    }

    /**
     * Get paged data (from cache or api). This will manage pulling from cache or the api. Cached data
     * will expire after 20 minutes
     */
    public getPaged<T extends JsonApiModel>(
        type: { new(): T; },
        apiRequestUrl: string,
        forceRefresh: boolean,
        displaySpinner: boolean = true,
        handleError: boolean = true,
        headers: HttpHeaders = this.getHeadersForJson(),
    ): Observable<PagedResponse> {
        const typeName: string = type.name;
        const cacheData: ICacheData = (this.cachedMap[typeName] || [])
            .find((c: ICacheData) => c.expires > Date.now() && c.url === apiRequestUrl);

        if (!forceRefresh && cacheData) {
            // if cache is available so just return it as `Observable`
            return of(cacheData.data.clone());
        }

        // Make the http request
        return this.get<IPagedResponse>(apiRequestUrl, displaySpinner, handleError, headers)
            .pipe(map((response: IPagedResponse) => {
                const json: any = response;
                // eslint-disable-next-line new-cap
                const events: T[] = json.data.map((d: any) => (new type()).deserialize(d, json.included));

                const pagedResponse: PagedResponse = new PagedResponse(
                    json.links.first,
                    json.links.last,
                    json.links.prev,
                    json.links.next,
                    json.meta.total,
                    events,
                );

                // Cache the results for the next 20 minutes
                this.cachedMap[typeName] = this.cachedMap[typeName] || [];
                const expires: number = Date.now() + HttpServiceHelper.CACHE_TIME;

                if (!cacheData) {
                    this.cachedMap[typeName].push({
                        url: apiRequestUrl,
                        data: pagedResponse.clone(),
                        expires: expires,
                    });
                } else {
                    cacheData.data = pagedResponse.clone();
                    cacheData.expires = expires;
                }

                return pagedResponse;
            }));
    }

    public clearCache<T extends JsonApiModel>(type: { new(): T; }): void {
        this.cachedMap[type.name] = [];
    }

    public get<T = IJsonApiResponse>(
        apiRequestUrl: string,
        displaySpinner: boolean = true,
        handleError: boolean = true,
        headers: HttpHeaders = this.getHeadersForJson(),
    ): Observable<T> {
        return this.request<T>(HttpRequestMethod.Get, apiRequestUrl, null, displaySpinner, handleError, headers);
    }

    public post<T = IJsonApiResponse>(
        apiRequestUrl: string,
        body: any,
        displaySpinner: boolean = true,
        handleError: boolean = true,
        headers: HttpHeaders = this.getHeadersForJson(),
    ): Observable<T> {
        return this.request<T>(HttpRequestMethod.Post, apiRequestUrl, body, displaySpinner, handleError, headers);
    }

    public patch<T = IJsonApiResponse>(
        apiRequestUrl: string,
        body: any,
        displaySpinner: boolean = true,
        handleError: boolean = true,
        headers: HttpHeaders = this.getHeadersForJson(),
    ): Observable<T> {
        return this.request<T>(HttpRequestMethod.Patch, apiRequestUrl, body, displaySpinner, handleError, headers);
    }

    // delete 204 without body
    public delete<T= IJsonApiResponse>(
        apiRequestUrl: string,
        displaySpinner: boolean = true,
        handleError: boolean = true,
        headers: HttpHeaders = this.getHeadersForJson(),
        body: string = null,
    ): Observable<Object> {
        return this.request<T>(HttpRequestMethod.Delete, apiRequestUrl, body, displaySpinner, handleError, headers);
    }

    public handleError(error: HttpErrorResponse, toastService?: ToastService): Observable<any> {
        if (error && error.status === 401) {
            // User is not authorized. Reloading the page will trigger the AuthGuard
            // and prompt the user to login again
            window.location.href = '/login';
            return;
        }

        if (toastService) {
            toastService.showError(HttpServiceHelper.getErrorMessage(error), '', -1);
        }

        return throwError(() => error);
    }

    public getHeadersForJson(includeAuth: boolean = true): HttpHeaders {
        let headers: HttpHeaders = new HttpHeaders();
        headers = headers.set('Content-Type', 'application/json');
        if (includeAuth) {
            const jwtToken: string = this.cookieService.get('jwt_token');
            if (jwtToken) {
                headers = headers.set('Authorization', `Bearer ${this.cookieService.get('jwt_token')}`);
            }
        }
        return headers;
    }

    public getQueryParameters(includes: string[], queryParams: string[] = null): string {
        let params: string = '';

        if (includes &&
            includes.length > 0) {
            params = `?include=${includes.join(',')}`;
        }

        if (queryParams &&
            queryParams.length > 0) {
            if (params.indexOf('?') === -1) {
                params += `?${queryParams.join('&')}`;
            } else {
                params += `&${queryParams.join('&')}`;
            }
        }

        return params;
    }

    private request<T>(
        method: HttpRequestMethod,
        apiRequestUrl: string,
        body: IKeyValue | string,
        displaySpinner: boolean = true,
        handleError: boolean = true,
        headers: HttpHeaders = this.getHeadersForJson(),
    ): Observable<T> {
        if (displaySpinner) {
            this.spinnerService.show();
        }

        return this.http.request(
            method,
            apiRequestUrl,
            {
                body: body,
                headers: headers,
            },
        )
            .pipe(
                finalize(() => {
                    if (displaySpinner) {
                        this.spinnerService.hide();
                    }
                }),
                catchError((error: HttpErrorResponse) => {
                    if (handleError) {
                        return this.handleError(error, this.toastService);
                    }
                    return throwError(() => error);
                }),
            );
    }
}

export enum HttpRequestMethod {
    Get = 'get',
    Post = 'post',
    Put = 'put',
    Delete = 'delete',
    Patch = 'patch',
}
